/*
Copyright (C) 2023-2026 QuantumNous

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

For commercial licensing, please contact support@quantumnous.com
*/
import { Link } from '@tanstack/react-router'
import { WandSparkles } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'

interface SetupWizardButtonProps {
  className?: string
}

/**
 * Top app-bar entry point for the setup wizard.
 *
 * Sits immediately after the search button in the header icon group and links
 * to the existing `/setup` route (the SetupWizard flow). Styled as a nova_portal
 * `.tb-icon` — a transparent 36px square with an accent hover — to match the
 * sibling header actions (notifications, language, theme, profile).
 */
export function SetupWizardButton({ className }: SetupWizardButtonProps) {
  const { t } = useTranslation()
  const label = t('Setup Wizard')

  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Button
            variant='ghost'
            size='icon'
            className={cn(
              'text-muted-foreground hover:bg-accent hover:text-accent-foreground h-9 w-9',
              className
            )}
            aria-label={label}
            render={<Link to='/setup' />}
          />
        }
      >
        <WandSparkles className='size-[1.2rem]' aria-hidden='true' />
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  )
}
